import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Share2, ChevronRight, Play, User, Calendar, Eye, Search, X, ChevronDown, ChevronUp, FileDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { authService } from '../services/authService';
import ShareModal from '../components/ShareModal';
import TranscriptModal from '../components/TranscriptModal';
import RightSideMenu from '../components/RightSideMenu';
import SubscriptionModal from '../components/SubscriptionModal';
import CreditStatus from '../components/CreditStatus';
import UnifiedHeader from '../components/UnifiedHeader';
import { fetchPublicVideos, incrementVideoView, getCreditStatus } from '../services/api';
import VideoReaction from '../components/VideoReaction';
import './PublicGalleryLoginView.css';

const PublicGalleryLoginView = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const creditStatusRef = useRef(null);
  
  // Video state
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);
  
  // Filter state
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // UI state
  const [showShare, setShowShare] = useState(false);
  const [shareVideo, setShareVideo] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcriptVideo, setTranscriptVideo] = useState(null);
  
  // Menu state
  const [showRightMenu, setShowRightMenu] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [creditStatus, setCreditStatus] = useState(null);
  
  const user = authService.getUser();
  const isAuthenticated = authService.isAuthenticated();
  const videosPerPage = 12;

  // Load credit status if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadCreditStatus();
    }
  }, [isAuthenticated]);

  const loadCreditStatus = async () => {
    try {
      const status = await getCreditStatus();
      setCreditStatus(status);
    } catch (err) {
      console.error('Error loading credit status:', err);
    }
  };

  const handleOpenSubscription = () => {
    setShowSubscriptionModal(true);
    document.body.classList.add('modal-open');
  };

  const handleCloseSubscription = () => {
    setShowSubscriptionModal(false);
    document.body.classList.remove('modal-open');
  };

  const handleSelectPlan = (plan) => {
    console.log('Selected plan:', plan);
  };

  // Fetch videos
  const fetchPublicVideosData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchPublicVideos({
        page: currentPage,
        limit: videosPerPage,
        sort: sortBy,
        subject: filterSubject,
        language: filterLanguage,
        search: searchQuery
      });
      
      setVideos(data.videos || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalVideos(data.pagination?.totalCount || 0);
    } catch (err) {
      console.error('Error fetching public videos:', err);
      setError(t.failedToLoad || 'Failed to load videos');
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy, filterSubject, filterLanguage, searchQuery, videosPerPage, t]);

  useEffect(() => {
    fetchPublicVideosData();
  }, [fetchPublicVideosData]);

  // Search
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Share
  const handleShare = (video) => {
    setShareVideo(video);
    setShowShare(true);
  };

  // Card expansion
  const handleCardClick = (videoId) => {
    setExpandedCard(expandedCard === videoId ? null : videoId);
  };

  // Play video
  const handlePlayVideo = (videoId) => {
    setPlayingVideo(videoId);
    incrementVideoView(videoId).catch(console.error);
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString(language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

   const getQualityDisplay = (quality) => {
    switch (quality) {
      case 'qk': return '4K';
      case 'qh': return '1080p';
      case 'qm': return '720p';
      case 'ql': return '480p';
      default: return quality;
    }
  };
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getSubjectColor = (subject) => {
    const colors = {
      chemistry: '#ef4444',
      physics: '#3b82f6',
      mathematics: '#10b981'
    };
    return colors[subject?.toLowerCase()] || '#6b7280';
  };
  
  const getSubjectName = (subject) => {
    if (!subject) return t.videoCard?.noSubject || 'General';
    
    const subjectMap = {
      'chemistry': t.chemistry || 'Chemistry',
      'physics': t.physics || 'Physics',
      'mathematics': t.mathematics || 'Mathematics',
    };
    
    return subjectMap[subject.toLowerCase()] || subject;
  };

  const getLanguageDisplay = (lang) => {
    const languages = { fi: 'Finnish', en: 'English', sv: 'Swedish' };
    return languages[lang] || lang?.toUpperCase();
  };
  
  // Transcript handler
  const handleTranscript = (video) => {
    setTranscriptVideo(video);
    setShowTranscript(true);
  };

  const handleCloseTranscript = () => {
    setShowTranscript(false);
    setTranscriptVideo(null);
  };
  

  return (
    <div className="public-gallery-login-view">
      <UnifiedHeader
        onOpenMenu={() => setShowRightMenu(true)}
        rightContent={isAuthenticated ? (
          <CreditStatus
            ref={creditStatusRef}
            compact={true}
            onUpgrade={handleOpenSubscription}
          />
        ) : null}
      />

      {/* Main Content */}
      <main className="gallery-login-main">
        <div className="gallery-login-container">
          
          {/* Page Title Section */}
          <div className="page-title-section">
            <h1 className="page-title">{t.publicGallery?.title || 'Community Gallery'}</h1>
            <p className="page-subtitle">{t.publicGallery?.subtitle || 'Explore public creations from our community'}</p>
          </div>

          {/* Search Section */}
          <div className="search-section">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-wrapper">
                <Search className="search-icon" size={20} />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={t.searchVideos || 'Search videos...'}
                  className="search-input"
                />
                {searchInput && (
                  <button type="button" onClick={clearSearch} className="clear-search-btn">
                    <X size={18} />
                  </button>
                )}
              </div>
              <button type="submit" className="search-button">
                {t.search || 'Search'}
              </button>
            </form>
          </div>

          {/* Gallery Controls */}
          <div className="gallery-controls">
            <div className="gallery-stats">
              <span className="total-count">{totalVideos} {t.publicGallery?.publicVideos || 'videos'}</span>
              {searchQuery && (
                <span className="search-indicator">
                  {t.searchResults || 'Search results for'}: "{searchQuery}"
                </span>
              )}
            </div>

            <div className="gallery-filters">
              <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }} className="filter-select">
                <option value="recent">{t.publicGallery?.mostRecent || 'Most Recent'}</option>
                <option value="popular">{t.publicGallery?.mostPopular || 'Most Popular'}</option>
                <option value="oldest">{t.publicGallery?.oldest || 'Oldest First'}</option>
              </select>

              <select value={filterSubject} onChange={(e) => { setFilterSubject(e.target.value); setCurrentPage(1); }} className="filter-select">
                <option value="all">{t.publicGallery?.allSubjects || 'All Subjects'}</option>
                <option value="chemistry">{t.chemistry || 'Chemistry'}</option>
                <option value="physics">{t.physics || 'Physics'}</option>
                <option value="mathematics">{t.mathematics || 'Mathematics'}</option>
              </select>

              <select value={filterLanguage} onChange={(e) => { setFilterLanguage(e.target.value); setCurrentPage(1); }} className="filter-select">
                <option value="all">{t.publicGallery?.allLanguages || 'All Languages'}</option>
                <option value="fi">🇫🇮 Suomi</option>
                <option value="en">🇬🇧 English</option>
                <option value="sv">🇸🇪 Svenska</option>
              </select>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>{t.loadingVideos || 'Loading videos...'}</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button onClick={fetchPublicVideosData} className="btn-retry">{t.retry || 'Retry'}</button>
            </div>
          )}

          {/* Empty */}
          {!loading && !error && videos.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🎬</div>
              <h2>{t.publicGallery?.noVideos || 'No Videos Found'}</h2>
              <p>{t.publicGallery?.noVideosDesc || 'Try adjusting your search or filters'}</p>
              {isAuthenticated && (
                <button onClick={() => navigate('/dashboard')} className="btn-create">
                  {t.createVideo || 'Create Video'}
                </button>
              )}
            </div>
          )}

          {/* Videos Grid */}
          {!loading && !error && videos.length > 0 && (
            <>
              <div className="videos-grid">
                {videos.map((video) => (
                  <div key={video.id} className={`video-card ${expandedCard === video.id ? 'expanded' : ''}`}>
                    {/* Thumbnail */}
                    <div className="video-thumbnail">
                      {playingVideo === video.id ? (
                        <video controls controlsList="nodownload" autoPlay className="inline-video-player" poster={video.thumbnail_url}>
                          <source src={video.video_url} type="video/mp4" />
                        </video>
                      ) : (
                        <>
                          {video.thumbnail_url ? (
                            <img src={video.thumbnail_url} alt={video.scene_name} />
                          ) : (
                            <div className="thumbnail-placeholder"><Play size={48} /></div>
                          )}
                          
                          <div className="video-overlay">
                            <button className="play-button" onClick={() => handlePlayVideo(video.id)}>
                              <Play size={24} />
                            </button>
                          </div>

                          {video.subject && (
                            <div className="subject-badge" style={{ background: getSubjectColor(video.subject) }}>
                              {getSubjectName(video.subject)}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Reaction bar */}
                    <VideoReaction videoId={video.id} showCounts compact />

                    {/* Video Info */}
                    <div className="video-info">
                      <h3 className="video-title">{video.scene_name}</h3>

                      <div className="video-meta-grid">
                        <div className="video-meta-item">
                          <span className="meta-item-label">{t.publicGallery?.creator?.toUpperCase() || 'CREATOR'}</span>
                          <div className="meta-item-value">
                            <User size={16} />
                            <span>{video.user_name || 'Anonymous'}</span>
                          </div>
                        </div>

                        <div className="video-meta-item">
                          <span className="meta-item-label">{t.publicGallery?.uploaded?.toUpperCase() || 'UPLOADED'}</span>
                          <div className="meta-item-value">
                            <Calendar size={16} />
                            <span>{formatDate(video.created_at)}</span>
                          </div>
                        </div>

                        <div className="video-meta-item">
                          <span className="meta-item-label">{t.publicGallery?.views?.toUpperCase() || 'VIEWS'}</span>
                          <div className="meta-item-value">
                            <Eye size={16} />
                            <span>{video.view_count || 0}</span>
                          </div>
                        </div>

                        <div className="video-meta-item">
                          <span className="meta-item-label">{t.publicGallery?.duration?.toUpperCase() || 'DURATION'}</span>
                          <div className="meta-item-value">
                            <Play size={16} />
                            <span>{formatDuration(video.duration)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="video-actions-bar">
                        <button 
                          className="action-btn share" 
                          onClick={() => handleShare(video)}
                          title={t.publicGallery?.share || 'Share'}
                        >
                          <Share2 size={20} strokeWidth={2} />
                        </button>

                        {video.transcript_markdown && (
                          <button 
                            className="action-btn transcript" 
                            onClick={() => handleTranscript(video)}
                            title={t.publicGallery?.transcript || 'View transcript'}
                          >
                            <FileDown size={20} strokeWidth={2} />
                          </button>
                        )}

                        <button 
                          className="action-btn info-toggle" 
                          onClick={() => handleCardClick(video.id)}
                          title={expandedCard === video.id ? (t.publicGallery?.hideDetails || 'Hide details') : (t.publicGallery?.showDetails || 'Show details')}
                        >
                          {expandedCard === video.id ? (
                            <ChevronUp size={20} strokeWidth={2} />
                          ) : (
                            <ChevronDown size={20} strokeWidth={2} />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expandable Details */}
                    {expandedCard === video.id && (
                      <div className="expanded-details">
                        <div className="details-section">
                          <h4>{t.videoDetails?.info?.toUpperCase() || 'INFO'}</h4>
                          <div className="details-grid">
                            <div className="detail-item">
                              <span className="detail-label">{t.videoDetails?.quality || 'Quality'}</span>
                              <span className="detail-value">{getQualityDisplay(video.quality)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">{t.videoDetails?.language || 'Language'}</span>
                              <span className="detail-value">{getLanguageDisplay(video.language)}</span>
                            </div>
                          </div>

                          {video.prompt && (
                            <>
                              <h4 style={{ marginTop: '1rem' }}>{t.videoCard?.prompt?.toUpperCase() || 'PROMPT'}</h4>
                              <div className="prompt-box">{video.prompt}</div>
                            </>
                          )}

                          <div className="details-actions">
                            <button 
                              onClick={() => navigate(`/video/${video.id}`)} 
                              className="btn-full-details"
                            >
                              {t.publicGallery?.openFullView || 'Open Full View'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="pagination-button">
                    <ChevronLeft size={20} />
                    <span>{t.previous || 'Previous'}</span>
                  </button>

                  <div className="pagination-info">
                    <span className="page-numbers">{currentPage} / {totalPages}</span>
                  </div>

                  <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-button">
                    <span>{t.next || 'Next'}</span>
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      {showShare && shareVideo && (
        <ShareModal isOpen={showShare} onClose={() => setShowShare(false)} videoId={shareVideo.id} sceneName={shareVideo.scene_name} />
      )}

      {showTranscript && transcriptVideo && (
        <TranscriptModal
          open={showTranscript}
          onClose={handleCloseTranscript}
          transcript={transcriptVideo.transcript_markdown}
        />
      )}

      {isAuthenticated && (
        <>
          <RightSideMenu isOpen={showRightMenu} onClose={() => setShowRightMenu(false)} user={user} currentPlan={creditStatus?.subscription_plan || 'free'} onOpenSubscription={handleOpenSubscription} />
          <SubscriptionModal isOpen={showSubscriptionModal} onClose={handleCloseSubscription} currentPlan={creditStatus?.subscription_plan || 'free'} onSelectPlan={handleSelectPlan} />
        </>
      )}
    </div>
  );
};

export default PublicGalleryLoginView;